import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Package, Wrench, Cog, HardHat, Check, X as XIcon, Loader2, Upload } from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { useAuth } from '@/contexts/AuthContext';
import { hasQuotePermission } from '@/lib/quotePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatMinor, majorToMinor, minorToMajor } from '@/lib/quoteMoney';
import { pickLocalizedField } from '@/i18n/localizeItem';
import { CatalogExcelImportDialog } from './CatalogExcelImportDialog';
import { CatalogBulkPriceDialog } from './CatalogBulkPriceDialog';
import { MasterCatalogReadyList } from './MasterCatalogReadyList';
import { CatalogHealthBanner } from './CatalogHealthBanner';

const API_URL = process.env.REACT_APP_API_URL;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICON = {
  product: Package,
  material: Cog,
  service: Wrench,
  labor: HardHat,
};

/**
 * Katalog ürün görseli URL çözümleyicisi. Öncelik sırası:
 *   1) Kullanıcının yüklediği özel görsel (`image_data_url`)
 *   2) PaydaAI hazır `image_key` görseli (backend static endpoint)
 *   3) null → UI bir placeholder ikonu gösterir.
 */
function resolveItemImage(it) {
  if (!it) return null;
  if (it.image_data_url) return it.image_data_url;
  if (it.image_key && typeof it.image_key === 'string' && it.image_key.includes('/')) {
    return `${BACKEND}/api/quote/catalog/assets/${it.image_key}.png`;
  }
  return null;
}

function emptyForm() {
  return {
    sku: '',
    sector_key: '',
    product_family_id: '',
    technical_attributes: {},
    tax_rate: '',
    item_type: 'product',
    name: '',
    description: '',
    category: '',
    unit: '',
    brand: '',                 // P4
    model: '',                 // P4
    unit_price_major: '',
    cost_price_major: '',
    currency: 'TRY',
    image_key: null,           // P7
    image_data_url: '',        // P7
  };
}

// P7 — Ürün görseli yükleme sabitleri (backend ile aynı sözleşme)
const IMAGE_MAX_BYTES = 500 * 1024;
const CATALOG_PAGE_SIZE = 50;
const IMAGE_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

export function QuoteCatalog() {
  const { t, i18n } = useTranslation('quotes');
  const { user } = useAuth();
  const showCosts = hasQuotePermission(user, 'costs');
  const lang = (i18n.language || 'tr').split('-')[0];
  const [catalogView, setCatalogView] = useState('ready');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [familyGroups, setFamilyGroups] = useState([]);
  const [familyFilter, setFamilyFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [classificationFilter, setClassificationFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkStatus, setBulkStatus] = useState('unchanged');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [activeSector, setActiveSector] = useState('');
  const [catalogProfileLoaded, setCatalogProfileLoaded] = useState(false);
  const [profileMasterCatalogEnabled, setProfileMasterCatalogEnabled] = useState(false);
  const [savedSystemKey, setSavedSystemKey] = useState('all');
  const [savedSubsystemKey, setSavedSubsystemKey] = useState('all');
  const [families, setFamilies] = useState([]);
  const [classification, setClassification] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/quote/industries`)
      .then(({ data }) => setIndustries(data.industries || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/quote/profile`, { params: { include_logo: false } })
      .then(({ data }) => {
        const sector = data.industry || 'other';
        setActiveSector(sector);
        setProfileMasterCatalogEnabled(Boolean(data.master_catalog_enabled));
        setCatalogView(data.master_catalog_enabled ? 'ready' : 'mine');
        setSavedSystemKey(data.catalog_system_key || 'all');
        setSavedSubsystemKey(data.catalog_subsystem_key || 'all');
        setCatalogProfileLoaded(true);
        setForm((f) => ({ ...f, sector_key: f.sector_key || sector }));
      })
      .catch(() => setCatalogProfileLoaded(true));
  }, []);

  useEffect(() => {
    if (!dialogOpen || !form.sector_key) return;
    axios.get(`${API_URL}/api/quote/catalog/taxonomy`, { params: { sector_key: form.sector_key } })
      .then(({ data }) => setFamilies(data.families || []))
      .catch(() => setFamilies([]));
  }, [dialogOpen, form.sector_key]);

  const selectedFamily = families.find((f) => f.id === form.product_family_id);
  const fieldLabels = {
    poe_ports: lang === 'en' ? 'PoE ports' : 'PoE portu',
    poe_budget_w: lang === 'en' ? 'PoE power budget (W)' : 'PoE güç bütçesi (W)',
    resolution_mp: lang === 'en' ? 'Resolution (MP)' : 'Çözünürlük (MP)',
    form_factor: lang === 'en' ? 'Camera type' : 'Kamera tipi',
    installation_environment: lang === 'en' ? 'Location' : 'Ortam',
    channels: lang === 'en' ? 'Channels' : 'Kanal sayısı',
    capacity_tb: lang === 'en' ? 'Capacity (TB)' : 'Kapasite (TB)',
    rack_units: lang === 'en' ? 'Rack units (U)' : 'Kabinet yüksekliği (U)',
  };
  const suggestFamily = async () => {
    if (!form.name.trim() || !form.sector_key || form.product_family_id) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/catalog/taxonomy/classify`, {
        name: form.name, item_type: form.item_type, sector_key: form.sector_key,
      });
      setClassification(data);
      if (data.classification_status === 'classified') {
        setForm((current) => current.product_family_id ? current : {
          ...current, product_family_id: data.product_family_id,
          technical_attributes: data.technical_attributes,
        });
      }
    } catch {
      setClassification(null);
    }
  };

  // P6 inline fiyat düzenleme durumu — hepsi id-anahtarlı, satır bazında bağımsız.
  //   priceDrafts[id] = "249,90"   (kullanıcının aktif taslak metni)
  //   savingIds       = Set<string>  (o an backend'e gitmiş kalemler)
  const [priceDrafts, setPriceDrafts] = useState({});
  const [savingIds, setSavingIds] = useState(() => new Set());
  // Çift-tetiklenme senkron kilidi: React state güncellemesi async;
  // hızlı çift Enter/klik'te aynı isteği iki kez atmayı önler.
  const inFlightRef = useRef(new Set());

  const loadFamilySummary = async () => {
    if (catalogView !== 'mine' || !activeSector) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/catalog/family-summary`, {
        params: { sector_key: activeSector },
      });
      setFamilyGroups(data.families || []);
    } catch {
      setFamilyGroups([]);
    }
  };

  // Re-check AI readiness when prices on the visible page change.
  const healthKey = useMemo(
    () => items.map((it) => `${it.id}:${it.unit_price_minor ?? ''}`).join('|'),
    [items],
  );

  const load = async () => {
    if (catalogView !== 'mine' || !activeSector) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/catalog`, {
        params: {
          q: query || undefined,
          sector_key: activeSector,
          item_type: filter !== 'all' ? filter : undefined,
          page_size: CATALOG_PAGE_SIZE,
          page,
          family_id: familyFilter === 'all' ? undefined : familyFilter,
          source: sourceFilter === 'all' ? undefined : sourceFilter,
          brand: brandFilter || undefined,
          classification_status: classificationFilter === 'all' ? undefined : classificationFilter,
          is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogView, filter, page, familyFilter, statusFilter, sourceFilter, classificationFilter, activeSector]);

  useEffect(() => {
    loadFamilySummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogView, activeSector]);

  const openNew = () => {
    setEditing(null);
    setForm((f) => ({ ...emptyForm(), sector_key: f.sector_key }));
    setClassification(null);
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      sku: it.sku || '',
      sector_key: it.sector_key || '',
      product_family_id: it.product_family_id || '',
      technical_attributes: it.technical_attributes || {},
      tax_rate: it.tax_rate ?? '',
      item_type: it.item_type || 'product',
      name: it.name || '',
      description: it.description || '',
      category: it.category || '',
      unit: it.unit || '',
      brand: it.brand || '',
      model: it.model || '',
      unit_price_major: minorToMajor(it.unit_price_minor).toString(),
      cost_price_major: minorToMajor(it.cost_price_minor || 0).toString(),
      currency: it.currency || 'TRY',
      image_key: it.image_key || null,
      image_data_url: it.image_data_url || '',
    });
    setClassification(null);
    setDialogOpen(true);
  };

  // P7 — Ürün özel görseli yükleme / kaldırma
  const imgFileRef = useRef(null);
  const [imageUploading, setImageUploading] = useState(false);

  const pickImage = () => imgFileRef.current?.click();

  const onImageChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // aynı dosyayı hata sonrası yeniden seçebilsin
    if (!file || !editing) return;
    if (!IMAGE_ALLOWED_TYPES.includes(file.type)) {
      toast.error(t('catalog.image.typeError'));
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error(t('catalog.image.sizeError'));
      return;
    }
    // Bu satıra özel senkron kilit — başka ürünlerin state'ini bozmaz.
    const id = editing.id;
    if (inFlightRef.current.has(`img:${id}`)) return;
    inFlightRef.current.add(`img:${id}`);
    setImageUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/quote/catalog/${id}/image`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      // Yalnızca ilgili ürünü yerinde güncelle; diğer satırlara dokunma.
      setItems((prev) => prev.map((row) => (row.id === id ? data : row)));
      setEditing(data);
      setForm((f) => ({
        ...f,
        image_key: data.image_key || null,
        image_data_url: data.image_data_url || '',
      }));
      toast.success(t('catalog.image.uploadSuccess'));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || t('catalog.image.uploadError')));
    } finally {
      inFlightRef.current.delete(`img:${id}`);
      setImageUploading(false);
    }
  };

  const removeCustomImage = async () => {
    if (!editing) return;
    const id = editing.id;
    if (inFlightRef.current.has(`img:${id}`)) return;
    inFlightRef.current.add(`img:${id}`);
    setImageUploading(true);
    try {
      const { data } = await axios.delete(`${API_URL}/api/quote/catalog/${id}/image`);
      setItems((prev) => prev.map((row) => (row.id === id ? data : row)));
      setEditing(data);
      setForm((f) => ({
        ...f,
        image_key: data.image_key || null,
        image_data_url: '',
      }));
      toast.success(t('catalog.image.removed'));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || t('catalog.image.removeError')));
    } finally {
      inFlightRef.current.delete(`img:${id}`);
      setImageUploading(false);
    }
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(t('catalog.form.validationName'));
      return;
    }
    const sale = String(form.unit_price_major || '0').trim().replace(',', '.');
    const cost = String(form.cost_price_major || '0').trim().replace(',', '.');
    if (![sale, cost].every((value) => /^\d+(?:\.\d{1,2})?$/.test(value))) {
      toast.error(t('catalog.form.validationPrice'));
      return;
    }
    const minor = majorToMinor(sale);
    setSaving(true);
    try {
      const payload = {
        sku: form.sku.trim() || null,
        ...(editing?.seeded_from ? {} : { sector_key: form.sector_key || 'other' }),
        ...(form.product_family_id ? {
          product_family_id: form.product_family_id,
          technical_attributes: form.technical_attributes,
        } : {}),
        tax_rate: form.tax_rate === '' ? null : Number(form.tax_rate),
        item_type: form.item_type,
        name: form.name.trim(),
        description: form.description,
        category: form.category,
        unit: form.unit,
        brand: form.brand || null,
        model: form.model || null,
        unit_price_minor: minor,
        cost_price_minor: majorToMinor(form.cost_price_major || 0),
        currency: (form.currency || 'TRY').toUpperCase(),
      };
      if (editing) {
        await axios.put(`${API_URL}/api/quote/catalog/${editing.id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/quote/catalog`, payload);
      }
      toast.success(t('common.saved'));
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(t('catalog.deleteConfirm', { name: it.name }))) return;
    try {
      await axios.delete(`${API_URL}/api/quote/catalog/${it.id}`);
      toast.success(t('catalog.deactivatedToast'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  // ---------------------------------------------------------------- P6 inline fiyat
  const setDraft = (id, value) => {
    setPriceDrafts((prev) => ({ ...prev, [id]: value }));
  };
  const clearDraft = (id) => {
    setPriceDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  /**
   * Bir satırın fiyatını hedef DB id'siyle günceller. Closure kaybını
   * önlemek için her zaman `id` parametresi alır — array index veya
   * paylaşılan state kullanmaz.
   */
  const savePrice = async (id, rawInput) => {
    if (!id) return;
    // Girişi ön-doğrula: boş / negatif / geçersiz metin — 0 açıkça kabul.
    const raw = String(rawInput ?? '').trim();
    if (raw === '') {
      toast.error(t('catalog.inlinePriceEmptyError'));
      return;
    }
    const normalized = raw.replace(',', '.');
    if (!/^\d+(\.\d{1,4})?$/.test(normalized)) {
      toast.error(t('catalog.inlinePriceInvalidError'));
      return;
    }
    // Senkron kilit — aynı satır için ikinci istek atmayı engelle.
    if (inFlightRef.current.has(id)) return;
    inFlightRef.current.add(id);
    setSavingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/quote/catalog/${id}/price`,
        { unit_price_major: raw },
      );
      // Yalnızca ilgili satırın güncel halini yerinde değiştir; diğer
      // satırlara ASLA dokunma.
      setItems((prev) => prev.map((row) => (row.id === id ? data : row)));
      clearDraft(id);
      toast.success(t('catalog.inlinePriceSaved'));
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || t('common.error');
      toast.error(typeof msg === 'string' ? msg : t('catalog.inlinePriceSaveError'));
      // Hata halinde taslak korunur; kullanıcı düzeltip tekrar deneyebilir.
    } finally {
      inFlightRef.current.delete(id);
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const FILTER_TABS = [
    { key: 'all', label: t('catalog.filterAll') },
    { key: 'product', label: t('catalog.filterProduct') },
    { key: 'material', label: t('catalog.filterMaterial') },
    { key: 'service', label: t('catalog.filterService') },
    { key: 'labor', label: t('catalog.filterLabor') },
  ];

  const missingPriceCount = items.filter((item) => item.is_active && Number(item.unit_price_minor || 0) <= 0).length;
  const categories = useMemo(
    () => [...new Set(items.map((item) => pickLocalizedField(item, 'category', lang)).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, lang)),
    [items, lang],
  );
  const duplicateSkus = useMemo(() => {
    const counts = new Map();
    items.forEach((item) => {
      const sku = (item.sku || '').trim().toUpperCase();
      if (sku) counts.set(sku, (counts.get(sku) || 0) + 1);
    });
    return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([sku]) => sku));
  }, [items]);
  const filteredItems = useMemo(
    () => items.filter((item) => {
      const category = pickLocalizedField(item, 'category', lang) || '';
      if (categoryFilter !== 'all' && category !== categoryFilter) return false;
      if (statusFilter === 'active' && !item.is_active) return false;
      if (statusFilter === 'inactive' && item.is_active) return false;
      const missing = Number(item.unit_price_minor || 0) <= 0;
      if (priceFilter === 'missing' && !missing) return false;
      if (priceFilter === 'priced' && missing) return false;
      if (priceFilter === 'duplicate' && !duplicateSkus.has((item.sku || '').trim().toUpperCase())) return false;
      return true;
    }),
    [items, categoryFilter, statusFilter, priceFilter, duplicateSkus, lang],
  );
  const allVisibleSelected = filteredItems.length > 0 && filteredItems.every((item) => selectedIds.has(item.id));

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filteredItems.forEach((item) => next.delete(item.id));
      else filteredItems.forEach((item) => next.add(item.id));
      return next;
    });
  };
  const applyBulkEdit = async () => {
    if (!selectedIds.size) return;
    const payload = { ids: [...selectedIds] };
    if (bulkCategory.trim()) payload.category = bulkCategory.trim();
    if (bulkStatus !== 'unchanged') payload.is_active = bulkStatus === 'active';
    if (payload.category === undefined && payload.is_active === undefined) {
      toast.error(lang === 'en' ? 'Choose a category or status change.' : 'Kategori veya durum değişikliği seçin.');
      return;
    }
    setBulkSaving(true);
    try {
      await axios.patch(`${API_URL}/api/quote/catalog/bulk`, payload);
      toast.success(lang === 'en' ? 'Selected items updated.' : 'Seçili kalemler güncellendi.');
      setSelectedIds(new Set());
      setBulkCategory('');
      setBulkStatus('unchanged');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <QuoteLayout>
      <div className="max-w-6xl mx-auto" data-testid="quote-catalog-page">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A2E]">{t('catalog.title')}</h1>
            <p className="text-sm text-[#595959] mt-1">
              {lang === 'en'
                ? 'Start from PaydaAI\'s ready catalog, then add your own products and prices.'
                : 'PaydaAI hazır kataloğundan başlayın; kendi ürün, marka, model ve fiyatlarınızı ekleyin.'}
            </p>
            <CatalogHealthBanner lang={lang} refreshKey={healthKey} />
          </div>
          {catalogView === 'mine' && (
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <CatalogBulkPriceDialog lang={lang} missingCount={missingPriceCount} onSaved={() => { load(); loadFamilySummary(); }} />
              <CatalogExcelImportDialog lang={lang} onImported={() => { load(); loadFamilySummary(); }} />
              <Button onClick={openNew} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="new-catalog-item-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('catalog.newButton')}
              </Button>
            </div>
          )}
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {catalogProfileLoaded && profileMasterCatalogEnabled && (
            <Button
              type="button"
              variant={catalogView === 'ready' ? 'default' : 'outline'}
              onClick={() => setCatalogView('ready')}
              data-testid="catalog-view-ready"
            >
              {lang === 'en' ? 'Ready Catalog' : 'Hazır Katalog'}
            </Button>
          )}
          <Button
            type="button"
            variant={catalogView === 'mine' ? 'default' : 'outline'}
            onClick={() => setCatalogView('mine')}
            data-testid="catalog-view-mine"
          >
            {lang === 'en' ? 'My Products' : 'Benim Ürünlerim'}
          </Button>
        </div>

        {catalogView === 'ready' && catalogProfileLoaded && (
          <MasterCatalogReadyList
            sectorKey={activeSector}
            lang={lang}
            initialSystemKey={savedSystemKey}
            initialSubsystemKey={savedSubsystemKey}
            onSelectionPersisted={({ systemKey, subsystemKey }) => {
              setSavedSystemKey(systemKey || 'all');
              setSavedSubsystemKey(subsystemKey || 'all');
            }}
          />
        )}

        {catalogView === 'mine' && (
          <>
        <div className="flex flex-wrap gap-2 mb-3">
          {FILTER_TABS.map((f) => (
            <button
              key={f.key}
              onClick={() => { setFilter(f.key); setPage(1); }}
              className={`px-3 py-1.5 text-sm rounded-full ${
                filter === f.key
                  ? 'bg-[#2563EB] text-white'
                  : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mb-3 flex gap-2 overflow-x-auto pb-2" aria-label={lang === 'en' ? 'Product families' : 'Ürün aileleri'}>
          <button type="button" className={`shrink-0 rounded-lg border px-3 py-2 text-sm ${familyFilter === 'all' ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}
            onClick={() => { setFamilyFilter('all'); setPage(1); setCategoryFilter('all'); }}>
            {lang === 'en' ? 'All families' : 'Tüm aileler'}
          </button>
          {familyGroups.slice().sort((a, b) => (lang === 'en' ? a.name_en : a.name).localeCompare(lang === 'en' ? b.name_en : b.name, lang)).map((group) => (
            <button key={group.id} type="button"
              className={`shrink-0 rounded-lg border px-3 py-2 text-sm ${familyFilter === group.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}
              onClick={() => { setFamilyFilter(group.id); setPage(1); setCategoryFilter('all'); }}>
              {lang === 'en' ? group.name_en : group.name} ({group.total})
            </button>
          ))}
        </div>

        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="catalog-category-filter"><SelectValue placeholder={lang === 'en' ? 'All categories' : 'Tüm kategoriler'} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'en' ? 'All categories' : 'Tüm kategoriler'}</SelectItem>
              {categories.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priceFilter} onValueChange={setPriceFilter}>
            <SelectTrigger data-testid="catalog-price-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'en' ? 'All price states' : 'Tüm fiyat durumları'}</SelectItem>
              <SelectItem value="priced">{lang === 'en' ? 'Priced' : 'Fiyatı girilmiş'}</SelectItem>
              <SelectItem value="missing">{lang === 'en' ? 'Missing price' : 'Fiyatı eksik'}</SelectItem>
              <SelectItem value="duplicate">{lang === 'en' ? 'Duplicate SKU' : 'Mükerrer SKU'}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger data-testid="catalog-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'en' ? 'All statuses' : 'Tüm durumlar'}</SelectItem>
              <SelectItem value="active">{lang === 'en' ? 'Active' : 'Aktif'}</SelectItem>
              <SelectItem value="inactive">{lang === 'en' ? 'Inactive' : 'Pasif'}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'en' ? 'All sources' : 'Tüm kaynaklar'}</SelectItem>
              <SelectItem value="manual">{lang === 'en' ? 'Manual' : 'Manuel'}</SelectItem>
              <SelectItem value="excel">{lang === 'en' ? 'Excel / CSV' : 'Excel / CSV'}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={classificationFilter} onValueChange={(v) => { setClassificationFilter(v); setPage(1); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'en' ? 'All AI states' : 'Tüm AI durumları'}</SelectItem>
              <SelectItem value="classified">{lang === 'en' ? 'Classified' : 'Sınıflandırılmış'}</SelectItem>
              <SelectItem value="needs_review">{lang === 'en' ? 'Needs review' : 'Kontrol gerekli'}</SelectItem>
              <SelectItem value="unclassified">{lang === 'en' ? 'Unclassified' : 'Sınıflandırılmamış'}</SelectItem>
            </SelectContent>
          </Select>
          <Input value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}
            onBlur={() => { setPage(1); load(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setPage(1); load(); } }}
            placeholder={lang === 'en' ? 'Brand filter' : 'Markaya göre filtrele'} />
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}
          className="mb-4 flex gap-2"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('catalog.searchPlaceholder')}
              className="pl-9"
              data-testid="catalog-search-input"
            />
          </div>
          <Button type="submit" variant="outline">{t('common.search')}</Button>
        </form>

        {selectedIds.size > 0 && (
          <div className="mb-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-end">
              <p className="text-sm font-semibold text-blue-900 md:mr-auto">
                {lang === 'en' ? `${selectedIds.size} selected` : `${selectedIds.size} kalem seçildi`}
              </p>
              <div className="min-w-[190px]">
                <Label className="text-xs">{lang === 'en' ? 'Set category' : 'Kategori ata'}</Label>
                <Input value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} placeholder={lang === 'en' ? 'Leave unchanged' : 'Değiştirmeden bırak'} />
              </div>
              <div className="min-w-[170px]">
                <Label className="text-xs">{lang === 'en' ? 'Set status' : 'Durum ata'}</Label>
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unchanged">{lang === 'en' ? 'Unchanged' : 'Değiştirme'}</SelectItem>
                    <SelectItem value="active">{lang === 'en' ? 'Active' : 'Aktif'}</SelectItem>
                    <SelectItem value="inactive">{lang === 'en' ? 'Inactive' : 'Pasif'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={applyBulkEdit} disabled={bulkSaving}>
                {bulkSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : (lang === 'en' ? 'Apply' : 'Uygula')}
              </Button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#595959]">{t('common.loading')}</div>
          ) : filteredItems.length === 0 ? (
            <div className="p-12 text-center text-[#94A3B8]" data-testid="catalog-empty">{t('catalog.empty')}</div>
          ) : (
            <>
              <div className="space-y-3 p-3 md:hidden" data-testid="catalog-mobile-list">
                {filteredItems.map((it) => {
                  const Icon = TYPE_ICON[it.item_type] || Package;
                  const imgUrl = resolveItemImage(it);
                  const isSaving = savingIds.has(it.id);
                  const draft = priceDrafts[it.id];
                  const inEdit = draft !== undefined;
                  const displayValue = inEdit ? draft : ((it.unit_price_minor || 0) > 0 ? minorToMajor(it.unit_price_minor).toString().replace('.', ',') : '');
                  return (
                    <article key={it.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={'catalog-mobile-card-' + it.id}>
                      <div className="flex items-start gap-3">
                        <input type="checkbox" checked={selectedIds.has(it.id)} onChange={() => toggleSelected(it.id)} className="mt-4 h-4 w-4" aria-label={lang === 'en' ? 'Select item' : 'Kalemi seç'} />
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                          {imgUrl ? <img src={imgUrl} alt={pickLocalizedField(it, 'name', lang) || (lang === 'en' ? 'Product' : 'Ürün')} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <Icon className="h-5 w-5 text-slate-400" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h2 className="font-semibold leading-5 text-slate-900">{pickLocalizedField(it, 'name', lang)}</h2>{!it.ai_eligible && <p className="mt-1 text-xs text-amber-700">{lang === 'en' ? 'Review family for AI use' : 'AI için ürün ailesini kontrol edin'}</p>}
                          <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-slate-500">
                            <span className="font-mono">{it.sku || 'SKU —'}</span><span>·</span>
                            <span>{pickLocalizedField(it, 'category', lang) || t('form.items.type' + ({ product: 'Product', material: 'Material', service: 'Service', labor: 'Labor' }[it.item_type] || 'Product'))}</span><span>·</span>
                            <span>{pickLocalizedField(it, 'unit', lang) || '—'}</span>
                          </div>
                          {(it.brand || it.model) && <p className="mt-1 text-xs text-slate-500">{[it.brand, it.model].filter(Boolean).join(' · ')}</p>}
                        </div>
                        <span className={'shrink-0 rounded-full px-2 py-1 text-[11px] ' + (it.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                          {it.is_active ? t('common.active') : t('common.inactive')}
                        </span>
                      </div>
                      <div className="mt-4">
                        <Label className="text-xs text-slate-600">{t('catalog.columns.unitPrice')}</Label>
                        <div className="mt-1 flex items-center gap-2">
                          <Input type="text" inputMode="decimal" value={displayValue} onChange={(e) => setDraft(it.id, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (inEdit && !isSaving) savePrice(it.id, draft); } else if (e.key === 'Escape') clearDraft(it.id); }}
                            placeholder="0,00" className="h-11 min-w-0 flex-1 text-right" disabled={isSaving} data-testid={'mobile-price-input-' + it.id} />
                          {inEdit && <>
                            <Button size="icon" variant="outline" className="h-11 w-11 shrink-0" disabled={isSaving} onClick={() => savePrice(it.id, draft)} aria-label={t('catalog.inlineSaveAria')}>
                              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 text-emerald-600" />}
                            </Button>
                            <Button size="icon" variant="ghost" className="h-11 w-11 shrink-0" disabled={isSaving} onClick={() => clearDraft(it.id)} aria-label={t('catalog.inlineCancelAria')}><XIcon className="h-4 w-4" /></Button>
                          </>}
                        </div>
                        {!inEdit && ((it.unit_price_minor || 0) > 0 ? <p className="mt-1 text-right text-xs text-slate-500">{formatMinor(it.unit_price_minor, it.currency)}</p> : <p className="mt-1 text-right text-xs font-medium text-amber-700">{t('catalog.inlineEmptyPrice')}</p>)}
                      </div>
                      <div className="mt-3 flex justify-end gap-2 border-t border-slate-100 pt-3">
                        <Button variant="outline" className="min-h-11" onClick={() => openEdit(it)}><Pencil className="mr-2 h-4 w-4" />{lang === 'en' ? 'Edit' : 'Düzenle'}</Button>
                        {it.is_active && <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => remove(it)} aria-label={lang === 'en' ? 'Deactivate' : 'Pasife al'}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                      </div>
                    </article>
                  );
                })}
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[#475569]">
                  <tr>
                    <th className="w-10 px-3 py-3"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} aria-label={lang === 'en' ? 'Select all visible' : 'Görünenlerin tümünü seç'} /></th>
                    <th className="text-left px-4 py-3 font-medium">{t('catalog.columns.name')}</th>
                    <th className="text-left px-4 py-3 font-medium">SKU</th>
                    <th className="text-left px-4 py-3 font-medium">{t('catalog.columns.type')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('catalog.columns.category')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('catalog.columns.unit')}</th>
                    <th className="text-right px-4 py-3 font-medium">{t('catalog.columns.unitPrice')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('catalog.columns.status')}</th>
                    <th className="text-right px-4 py-3 font-medium">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {filteredItems.map((it) => {
                    const Icon = TYPE_ICON[it.item_type] || Package;
                    const imgUrl = resolveItemImage(it);
                    const isSaving = savingIds.has(it.id);
                    const draft = priceDrafts[it.id];
                    const inEdit = draft !== undefined;
                    const displayValue = inEdit
                      ? draft
                      : ((it.unit_price_minor || 0) > 0
                          ? minorToMajor(it.unit_price_minor).toString().replace('.', ',')
                          : '');
                    return (
                      <tr key={it.id} data-testid={`catalog-row-${it.id}`}>
                        <td className="px-3 py-3"><input type="checkbox" checked={selectedIds.has(it.id)} onChange={() => toggleSelected(it.id)} aria-label={lang === 'en' ? 'Select item' : 'Kalemi seç'} /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                              {imgUrl ? (
                                <img
                                  src={imgUrl}
                                  alt={pickLocalizedField(it, 'name', lang) || 'Ürün'}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  data-testid={`catalog-image-${it.id}`}
                                />
                              ) : (
                                <Icon className="w-5 h-5 text-[#94A3B8]" />
                              )}
                            </div>
                            <div>
                              <span className="font-medium text-[#1A1A2E]">{pickLocalizedField(it, 'name', lang)}</span>{!it.ai_eligible && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700">{lang === 'en' ? 'Needs AI review' : 'AI kontrolü gerekli'}</span>}
                              {it.description && (
                                <p className="text-xs text-[#94A3B8]">{it.description}</p>
                              )}
                              {(it.brand || it.model) && (
                                <p className="text-[11px] text-[#94A3B8]">
                                  {it.brand && <span>{lang === 'en' ? 'Brand' : 'Marka'}: <b>{it.brand}</b></span>}
                                  {it.brand && it.model && <span> · </span>}
                                  {it.model && <span>Model: <b>{it.model}</b></span>}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{it.sku || '—'}</td>
                        <td className="px-4 py-3 text-[#595959]">
                          {t(`form.items.type${{ product: 'Product', material: 'Material', service: 'Service', labor: 'Labor' }[it.item_type] || 'Product'}`)}
                        </td>
                        <td className="px-4 py-3 text-[#595959]">{pickLocalizedField(it, 'category', lang) || '—'}</td>
                        <td className="px-4 py-3 text-[#595959]">{pickLocalizedField(it, 'unit', lang) || '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-2 justify-end">
                            <div className="flex items-center gap-1">
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={displayValue}
                                onChange={(e) => setDraft(it.id, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (inEdit && !isSaving) savePrice(it.id, draft);
                                  } else if (e.key === 'Escape') {
                                    clearDraft(it.id);
                                  }
                                }}
                                placeholder="0,00"
                                className="h-8 w-28 text-right"
                                disabled={isSaving}
                                data-testid={`price-input-${it.id}`}
                              />
                              {inEdit ? (
                                <>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    disabled={isSaving}
                                    onClick={() => savePrice(it.id, draft)}
                                    data-testid={`price-save-${it.id}`}
                                    aria-label={t('catalog.inlineSaveAria')}
                                  >
                                    {isSaving
                                      ? <Loader2 className="w-4 h-4 animate-spin" />
                                      : <Check className="w-4 h-4 text-emerald-600" />}
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    disabled={isSaving}
                                    onClick={() => clearDraft(it.id)}
                                    aria-label={t('catalog.inlineCancelAria')}
                                  >
                                    <XIcon className="w-4 h-4 text-[#94A3B8]" />
                                  </Button>
                                </>
                              ) : (
                                (it.unit_price_minor || 0) > 0 ? (
                                  <span className="text-xs text-[#94A3B8] whitespace-nowrap" data-testid={`price-formatted-${it.id}`}>
                                    {formatMinor(it.unit_price_minor, it.currency)}
                                  </span>
                                ) : (
                                  <span className="text-[11px] italic text-amber-600 whitespace-nowrap" data-testid={`price-unset-${it.id}`}>
                                    {t('catalog.inlineEmptyPrice')}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${it.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>
                            {it.is_active ? t('common.active') : t('common.inactive')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`edit-catalog-${it.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {it.is_active && (
                              <Button variant="ghost" size="icon" onClick={() => remove(it)} data-testid={`delete-catalog-${it.id}`}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {total > CATALOG_PAGE_SIZE && <div className="mt-3 flex items-center justify-end gap-3 text-sm">
          <span>{lang === 'en' ? `Page ${page} of ${Math.ceil(total / CATALOG_PAGE_SIZE)}` : `${page} / ${Math.ceil(total / CATALOG_PAGE_SIZE)} sayfa`}</span>
          <Button type="button" variant="outline" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>{lang === 'en' ? 'Previous' : 'Önceki'}</Button>
          <Button type="button" variant="outline" disabled={page * CATALOG_PAGE_SIZE >= total} onClick={() => setPage((n) => n + 1)}>{lang === 'en' ? 'Next' : 'Sonraki'}</Button>
        </div>}

          </>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-h-[calc(100dvh-1rem)] overflow-y-auto pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:max-h-[90vh]" data-testid="catalog-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? t('catalog.form.titleEdit') : t('catalog.form.titleNew')}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* P7 — Ürün görseli (Corporate V2'de gizli, Ürün Görselli PDF'te önceliklidir) */}
              {editing && (
                <div className="md:col-span-2 border border-[#E5E5E5] rounded-lg p-3 flex items-center gap-3 bg-[#F8FAFC]" data-testid="catalog-image-panel">
                  <div className="w-16 h-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                    {resolveItemImage(form) ? (
                      <img
                        src={resolveItemImage(form)}
                        alt={form.name || (lang === 'en' ? 'Product' : 'Ürün')}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        data-testid="catalog-image-preview"
                      />
                    ) : (
                      <Package className="w-6 h-6 text-[#94A3B8]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1A1A2E]">{t('catalog.image.title')}</div>
                    <div className="text-[11px] text-[#94A3B8]">
                      {form.image_data_url
                        ? t('catalog.image.custom')
                        : (form.image_key
                            ? t('catalog.image.preset')
                            : t('catalog.image.none'))}
                    </div>
                    <div className="text-[10px] text-[#94A3B8]">
                      {t('catalog.image.limits')}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <input
                      ref={imgFileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={onImageChosen}
                      className="hidden"
                      data-testid="catalog-image-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={pickImage}
                      disabled={imageUploading}
                      data-testid="catalog-image-upload-btn"
                    >
                      {imageUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      {form.image_data_url ? t('catalog.image.change') : t('catalog.image.upload')}
                    </Button>
                    {form.image_data_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeCustomImage}
                        disabled={imageUploading}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="catalog-image-remove-btn"
                      >
                        <XIcon className="w-4 h-4 mr-1" />
                        {t('catalog.image.removeCustom')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              <div>
                <Label>{lang === 'en' ? 'Sector' : 'Sektör'}</Label>
                <Select value={form.sector_key || 'other'} onValueChange={(value) => {
                  setForm({ ...form, sector_key: value, product_family_id: '', technical_attributes: {} });
                  setClassification(null);
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{industries.map((industry) => <SelectItem key={industry.key} value={industry.key}>{industry.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('catalog.form.type')}</Label>
                <Select value={form.item_type} onValueChange={(v) => {
                  setForm({ ...form, item_type: v, product_family_id: '', technical_attributes: {} });
                  setClassification(null);
                }}>
                  <SelectTrigger data-testid="catalog-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">{t('form.items.typeProduct')}</SelectItem>
                    <SelectItem value="material">{t('form.items.typeMaterial')}</SelectItem>
                    <SelectItem value="service">{t('form.items.typeService')}</SelectItem>
                    <SelectItem value="labor">{t('form.items.typeLabor')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('catalog.form.category')}</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </div>
              <div>
                <Label>{lang === 'en' ? 'SKU / Product Code' : 'SKU / Ürün Kodu'}</Label>
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} maxLength={80} data-testid="catalog-sku-input" />
              </div>
              <div>
                <Label>{t('catalog.form.name')}</Label>
                <Input value={form.name} onChange={(e) => {
                  setForm({ ...form, name: e.target.value, product_family_id: '', technical_attributes: {} });
                  setClassification(null);
                }} onBlur={suggestFamily} data-testid="catalog-name-input" />
              </div>
              <div className="md:col-span-2">
                <Label>{lang === 'en' ? 'Product family' : 'Ürün ailesi'}</Label>
                <Select value={form.product_family_id || 'none'} onValueChange={(value) => {
                  setForm({ ...form, product_family_id: value === 'none' ? '' : value, technical_attributes: {} });
                  setClassification(null);
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{lang === 'en' ? 'Not selected' : 'Seçilmedi'}</SelectItem>
                    {families.filter((f) => (f.item_type === form.item_type || (form.item_type === 'material' && f.item_type === 'product'))).map((family) => (
                      <SelectItem key={family.id} value={family.id}>{lang === 'en' ? family.name_en : family.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {classification && <p className="mt-1 text-xs text-amber-700">
                  {classification.classification_status === 'classified'
                    ? (lang === 'en' ? 'Family and known specifications suggested; please check.' : 'Aile ve bilinen özellikler önerildi; kontrol edin.')
                    : (lang === 'en' ? 'Family needs review. Select it to use this item in AI quotes.' : 'Aile kontrol gerektiriyor. AI teklifinde kullanmak için seçin.')}
                </p>}
              </div>
              {selectedFamily?.technical_fields.map((field) => (
                <div key={field.key}>
                  <Label>{fieldLabels[field.key] || field.key}{field.required ? ' *' : ''}</Label>
                  {field.type === 'enum' || field.type === 'boolean' ? (
                    <Select value={String(form.technical_attributes[field.key] ?? 'unset')} onValueChange={(value) =>
                      setForm({ ...form, technical_attributes: {
                        ...form.technical_attributes,
                        [field.key]: value === 'unset' ? null : field.type === 'boolean' ? value === 'true' : value,
                      } })
                    }>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="unset">—</SelectItem>
                        {(field.allowed || (field.type === 'boolean' ? [true, false] : [])).map((v) => <SelectItem key={String(v)} value={String(v)}>{String(v)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <Input type="number" min="0" step={field.type === 'integer' ? '1' : 'any'}
                    value={form.technical_attributes[field.key] ?? ''}
                    onChange={(event) => setForm({ ...form, technical_attributes: {
                      ...form.technical_attributes,
                      [field.key]: event.target.value === '' ? null : Number(event.target.value),
                    } })} />}
                </div>
              ))}
              <div>
                <Label>{lang === 'en' ? 'VAT rate (%)' : 'KDV oranı (%)'}</Label>
                <Input type="number" min="0" max="100" step="0.01" value={form.tax_rate}
                  onChange={(event) => setForm({ ...form, tax_rate: event.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>{t('catalog.form.description')}</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <Label>{t('catalog.form.unit')}</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              </div>
              <div>
                <Label>{t('catalog.form.currency')}</Label>
                <Input maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <Label>{t('catalog.form.brandOptional')}</Label>
                <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="catalog-brand-input" />
              </div>
              <div>
                <Label>{t('catalog.form.modelOptional')}</Label>
                <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="catalog-model-input" />
              </div>
              <div className="md:col-span-2">
                <Label>{t('catalog.form.unitPrice')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.unit_price_major}
                  onChange={(e) => setForm({ ...form, unit_price_major: e.target.value })}
                  data-testid="catalog-price-input"
                />
                <p className="text-xs text-[#94A3B8] mt-1">{t('catalog.form.unitPriceHint')}</p>
              </div>
              {showCosts && <div className="md:col-span-2">
                <Label>{t('catalog.form.cost')} <span className="text-[#94A3B8] text-xs">{t('catalog.form.costOptional')}</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cost_price_major || ''}
                  onChange={(e) => setForm({ ...form, cost_price_major: e.target.value })}
                  placeholder="0.00"
                  data-testid="catalog-cost-input"
                />
                <p className="text-xs text-[#94A3B8] mt-1">{t('catalog.form.costHint')}</p>
              </div>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button
                onClick={submit}
                disabled={saving}
                className="bg-[#2563EB] hover:bg-[#1E4ED8]"
                data-testid="catalog-save-btn"
              >
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </QuoteLayout>
  );
}

export default QuoteCatalog;
